import React from "react";
import { FileDown, RotateCcw, X } from "lucide-react";
import { ORDER_PDF_COLUMN_CATALOG } from "../utils/dataManagerOrdersCustomization.js";

const PRIMARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full bg-accent px-2.75 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT_CLASS_NAME = "h-8 w-full rounded-sm border border-slate-200 bg-white px-2.5 text-[11.5px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent disabled:bg-slate-50 disabled:text-slate-400";

export default function DataManagerOrdersPdfDialog({
  columnLabels = {},
  columns = ORDER_PDF_COLUMN_CATALOG,
  countLabel = "",
  exportLabel = "Exporter PDF",
  exportingLabel = "Export...",
  eyebrow = "Export PDF",
  isExporting = false,
  onClose = null,
  onExport = null,
  onLabelChange = null,
  onReset = null,
  onToggleColumn = null,
  resultSummaryLabel = "",
  selectedColumnKeys = [],
  title = "Colonnes du rapport Commandes",
  visibleOrderCount = 0,
}) {
  const selectedKeySet = React.useMemo(
    () => new Set(Array.isArray(selectedColumnKeys) ? selectedColumnKeys : []),
    [selectedColumnKeys],
  );
  const selectedCount = selectedKeySet.size;

  return (
    <div className="fixed inset-0 z-80 flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="Fermer la configuration PDF"
        className="absolute inset-0 bg-slate-950/45"
        onClick={onClose}
      />
      <div role="dialog" aria-modal="true" aria-label={title} className="relative mx-auto flex max-h-[min(38rem,calc(100dvh-8rem))] w-full max-w-4xl flex-col overflow-hidden rounded-[4px] border border-black/10 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</p>
            <h3 className="mt-1 text-[1rem] font-semibold tracking-[-0.02em] text-slate-950">{title}</h3>
            <p className="mt-1 text-[11.5px] leading-5 text-slate-600">
              {countLabel || `${visibleOrderCount} commande(s)`}{resultSummaryLabel ? ` · ${resultSummaryLabel}` : ""} · {selectedCount} colonne(s)
            </p>
          </div>
          <button type="button" onClick={onClose} className={SECONDARY_ACTION_CLASS_NAME}>
            <X size={13} />
            Fermer
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-3">
          <div className="grid gap-1.5">
            {columns.map((column) => {
              const isSelected = selectedKeySet.has(column.key);
              return (
                <div key={column.key} className="grid grid-cols-[minmax(10rem,14rem)_minmax(0,1fr)] items-center gap-2 border-b border-slate-100 py-1.5 last:border-b-0">
                  <label className="flex min-w-0 items-center gap-2 text-[11.5px] font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(event) => onToggleColumn?.(column.key, event.target.checked)}
                      className="h-4 w-4 shrink-0 rounded border-slate-300 text-accent focus:ring-accent"
                    />
                    <span className="truncate">{column.label}</span>
                  </label>
                  <input
                    type="text"
                    value={columnLabels[column.key] || column.label}
                    onChange={(event) => onLabelChange?.(column.key, event.target.value)}
                    disabled={!isSelected}
                    className={INPUT_CLASS_NAME}
                    aria-label={`Nom de colonne PDF ${column.label}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={onReset} className={SECONDARY_ACTION_CLASS_NAME}>
            <RotateCcw size={13} />
            Réinitialiser
          </button>
          <div className="flex flex-wrap items-center gap-1.5">
            <button type="button" onClick={onClose} className={SECONDARY_ACTION_CLASS_NAME}>
              Annuler
            </button>
            <button
              type="button"
              onClick={onExport}
              disabled={selectedCount === 0 || isExporting}
              className={PRIMARY_ACTION_CLASS_NAME}
            >
              <FileDown size={13} />
              {isExporting ? exportingLabel : exportLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
